import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { isStudentsBuild } from '../config/AppStatic';
import { getLastInstitutionUrlEndpoint } from '../utils/lastInstitutionUrlEndpoint';

const KNOWN_INSTITUTIONS = ['Catalyst'];

function WelcomePage() {
    const navigate = useNavigate();
    const savedEndpoint = useMemo(() => getLastInstitutionUrlEndpoint(), []);
    const [institutionName, setInstitutionName] = useState('');
    const [error, setError] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const inputRef = useRef(null);

    useEffect(() => {
        if (savedEndpoint) {
            navigate(`/${savedEndpoint}`, { replace: true });
        }
    }, [savedEndpoint, navigate]);

    const getInstitutionSuggestions = useCallback((input) => {
        const normalizedInput = input.toLowerCase().trim();
        if (!normalizedInput) return [];
        return KNOWN_INSTITUTIONS.filter(institution =>
            institution.toLowerCase().startsWith(normalizedInput)
        );
    }, []);

    const handleInputChange = useCallback((e) => {
        const value = e.target.value;
        setInstitutionName(value);
        setError('');

        const newSuggestions = getInstitutionSuggestions(value);
        setSuggestions(newSuggestions);
        const shouldShow = newSuggestions.length > 0 && value.length > 0;
        setShowSuggestions(shouldShow);
        // Pre-select the first suggestion so Enter/Tab accepts it intuitively.
        setHighlightedIndex(shouldShow ? 0 : -1);
    }, [getInstitutionSuggestions]);

    const selectSuggestion = useCallback((suggestion) => {
        setInstitutionName(suggestion);
        setSuggestions([]);
        setShowSuggestions(false);
        setHighlightedIndex(-1);
    }, []);

    // The "ghost text" portion: the remaining characters of the highlighted
    // suggestion beyond what the user has already typed. Preserves the user's
    // case in the typed portion.
    const ghostCompletion = useMemo(() => {
        if (!showSuggestions || highlightedIndex < 0) return '';
        const suggestion = suggestions[highlightedIndex];
        if (!suggestion) return '';
        if (suggestion.toLowerCase().startsWith(institutionName.toLowerCase())
            && suggestion.length > institutionName.length) {
            return suggestion.slice(institutionName.length);
        }
        return '';
    }, [showSuggestions, highlightedIndex, suggestions, institutionName]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'ArrowDown') {
            if (showSuggestions && suggestions.length > 0) {
                e.preventDefault();
                setHighlightedIndex(i => (i + 1) % suggestions.length);
            }
            return;
        }
        if (e.key === 'ArrowUp') {
            if (showSuggestions && suggestions.length > 0) {
                e.preventDefault();
                setHighlightedIndex(i => (i <= 0 ? suggestions.length - 1 : i - 1));
            }
            return;
        }
        if (e.key === 'Escape') {
            setShowSuggestions(false);
            setHighlightedIndex(-1);
            return;
        }
        // If suggestions are open and one is highlighted, Enter accepts it
        // (rather than submitting the partial text). The form onSubmit is a
        // safety net that auto-completes unique prefixes anyway.
        if (e.key === 'Enter' && showSuggestions && highlightedIndex >= 0
            && suggestions[highlightedIndex]
            && suggestions[highlightedIndex].toLowerCase() !== institutionName.toLowerCase()) {
            e.preventDefault();
            selectSuggestion(suggestions[highlightedIndex]);
            return;
        }
        // Tab or Right-arrow (only at end of input) accepts the ghost completion.
        if (ghostCompletion && (e.key === 'Tab'
            || (e.key === 'ArrowRight'
                && inputRef.current
                && inputRef.current.selectionStart === institutionName.length
                && inputRef.current.selectionEnd === institutionName.length))) {
            e.preventDefault();
            selectSuggestion(suggestions[highlightedIndex]);
        }
    }, [showSuggestions, suggestions, ghostCompletion, highlightedIndex, institutionName, selectSuggestion]);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        let value = (institutionName || '').trim();
        if (!value) {
            setError('Please enter your institution name');
            return;
        }
        // Safety net: if the typed value is a unique prefix of a known
        // institution, auto-complete before navigating. This prevents the
        // common mistake of pressing Enter after typing only a few letters.
        const matches = getInstitutionSuggestions(value);
        if (matches.length === 1) {
            value = matches[0];
            setInstitutionName(value);
        }
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        const normalized = value.replace(/\s+/g, '-').toLowerCase();
        navigate(`/${normalized}`);
    }, [institutionName, navigate, getInstitutionSuggestions]);

    if (savedEndpoint) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white px-4">
                <p className="text-gray-600 text-sm">Loading your schedule…</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-white px-4">
            <div className="w-full max-w-md">
                <h1 className="text-2xl font-semibold text-gray-900 mb-6 text-center">Welcome to Pi-Schedule for {isStudentsBuild() ? 'Students' : 'Teachers'}</h1>
                <p className="text-gray-600 mb-6 text-center">Enter your institution name to continue</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="relative">
                        <label htmlFor="institution" className="block text-sm font-medium text-gray-700 mb-1">Institution name</label>
                        <div className="relative">
                            {/* Ghost-text overlay: renders the typed text invisibly then the
                                remaining suggestion in gray, so it lines up under the real input. */}
                            {ghostCompletion && (
                                <div
                                    aria-hidden="true"
                                    className="pointer-events-none absolute inset-0 px-3 py-2 text-gray-400 whitespace-pre overflow-hidden rounded-md"
                                >
                                    <span className="invisible">{institutionName}</span>
                                    <span>{ghostCompletion}</span>
                                </div>
                            )}
                            <input
                                ref={inputRef}
                                id="institution"
                                type="text"
                                value={institutionName}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                onFocus={() => {
                                    if (suggestions.length > 0) {
                                        setShowSuggestions(true);
                                        if (highlightedIndex < 0) setHighlightedIndex(0);
                                    }
                                }}
                                className="relative w-full bg-transparent border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                                autoComplete="off"
                                spellCheck={false}
                                aria-autocomplete="both"
                                aria-expanded={showSuggestions}
                            />
                        </div>
                        {showSuggestions && suggestions.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                                {suggestions.map((suggestion, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => selectSuggestion(suggestion)}
                                        className={`w-full text-left px-3 py-2 focus:outline-none first:rounded-t-md last:rounded-b-md ${index === highlightedIndex ? 'bg-blue-100 text-blue-900' : 'hover:bg-gray-100'}`}
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        )}
                        {ghostCompletion && (
                            <p className="mt-1 text-xs text-gray-500">
                                Press <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded">Tab</kbd> or <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded">Enter</kbd> to complete “{suggestions[highlightedIndex]}”
                            </p>
                        )}
                        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
                    </div>
                    <button type="submit" className="w-full bg-blue-600 text-white rounded-md px-4 py-2 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">Continue</button>
                </form>
            </div>
        </div>
    );
}

export default WelcomePage;
